// src/features/system/edge-tasks/components/EdgeMetricsPanel.tsx
import React, { useState } from 'react';
import { useEdgeMetrics } from '../hooks/useEdgeMetrics';
import { timeAgo } from '@/utils/date';
import type { EdgeMetricsSnapshot, DiskSnapshot, NetworkSnapshot } from '../api/edgeMetricsApi';

// ── Helpers ─────────────────────────────────────────────────────────────

function Gauge({ label, value, unit = '%', color = 'indigo' }: {
  label: string; value: number | null; unit?: string; color?: string;
}) {
  const pct = value ?? 0;
  const barColor = {
    indigo: 'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
  }[color] ?? 'bg-indigo-500';
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-neutral-400">{label}</span>
        <span className="font-mono text-neutral-100">
          {value != null ? `${value.toFixed(1)}${unit}` : '—'}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-neutral-800 overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="pac-glass rounded-lg px-3 py-2">
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-mono text-neutral-100 mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

function DiskRow({ d }: { d: DiskSnapshot }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-neutral-300 w-16 shrink-0">{d.mount}</span>
      <div className="flex-1">
        <Gauge label="" value={d.usagePercent} color={
          (d.usagePercent ?? 0) > 90 ? 'rose' : (d.usagePercent ?? 0) > 70 ? 'amber' : 'emerald'
        } />
      </div>
      <span className="text-[10px] text-neutral-500 w-20 text-right">
        {d.freeGb != null ? `${d.freeGb} GB free` : '—'}
      </span>
    </div>
  );
}

function NetworkRow({ n }: { n: NetworkSnapshot }) {
  const fmt = (b: number) => b > 1e9 ? `${(b / 1e9).toFixed(1)} GB`
    : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`;
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="font-mono text-neutral-300 w-24 truncate" title={n.name}>{n.name}</span>
      <span className="text-emerald-400">↑ {fmt(n.bytesSent)}</span>
      <span className="text-indigo-400">↓ {fmt(n.bytesReceived)}</span>
      {n.speedBps > 0 && (
        <span className="text-neutral-500 ml-auto">{(n.speedBps / 1e6).toFixed(0)} Mbps</span>
      )}
    </div>
  );
}

// ── Snapshot detail view ────────────────────────────────────────────────

function SnapshotView({ s }: { s: EdgeMetricsSnapshot }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Top stats */}
      <div className="col-span-full grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="Hostname" value={s.hostname || '—'} />
        <StatCard label="Agent" value={s.agentId || '—'} />
        <StatCard label="CPU Arch" value={s.cpuArch || '—'} />
        <StatCard label="Cores" value={s.cpuCores ?? '—'} />
      </div>

      {/* CPU gauge */}
      <div className="pac-glass rounded-lg p-4 space-y-2">
        <h4 className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">CPU</h4>
        <Gauge label="Usage" value={s.cpu} color={(s.cpu ?? 0) > 80 ? 'rose' : 'indigo'} />
      </div>

      {/* Memory gauge */}
      <div className="pac-glass rounded-lg p-4 space-y-2">
        <h4 className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Memory</h4>
        <Gauge label="Usage" value={s.memoryPercent} color={
          (s.memoryPercent ?? 0) > 85 ? 'rose' : (s.memoryPercent ?? 0) > 60 ? 'amber' : 'emerald'
        } />
        <div className="text-[10px] text-neutral-500">
          {s.memoryUsedMb != null && s.memoryTotalMb != null
            ? `${s.memoryUsedMb.toLocaleString()} / ${s.memoryTotalMb.toLocaleString()} MB`
            : '—'}
        </div>
      </div>

      {/* Disks */}
      {s.disks.length > 0 && (
        <div className="pac-glass rounded-lg p-4 space-y-2">
          <h4 className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Disks</h4>
          <div className="space-y-2">
            {s.disks.map((d, i) => <DiskRow key={d.mount || i} d={d} />)}
          </div>
        </div>
      )}

      {/* Network */}
      {s.network.length > 0 && (
        <div className="pac-glass rounded-lg p-4 space-y-2">
          <h4 className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Network</h4>
          <div className="space-y-1">
            {s.network.map((n, i) => <NetworkRow key={n.name || i} n={n} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────

export default function EdgeMetricsPanel() {
  const { data, isLoading, error, collect } = useEdgeMetrics();
  const [sampleMs, setSampleMs] = useState(2000);

  const latest = data?.latest ?? null;
  const snapshots = data?.snapshots ?? [];

  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      {/* Toolbar */}
      <div className="pac-glass rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Sample duration (ms)
          <input
            type="number" min={500} step={500} value={sampleMs}
            onChange={(e) => setSampleMs(Number(e.target.value) || 2000)}
            className="bg-neutral-800/70 border border-neutral-700 rounded px-2 py-1 text-sm text-neutral-100 w-28"
          />
        </label>
        <button
          disabled={collect.isPending}
          onClick={() => collect.mutate({ sampleMs })}
          className="px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-sm font-medium text-white disabled:opacity-40 transition-colors"
        >
          {collect.isPending ? 'Collecting…' : 'Collect Now'}
        </button>
        {collect.isSuccess && (
          <span className="text-[10px] text-emerald-400">Task dispatched — results will appear below</span>
        )}
        {collect.isError && (
          <span className="text-[10px] text-red-400">
            {(collect.error as Error)?.message ?? 'Dispatch failed'}
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-400 px-2">Failed to load metrics: {error.message}</div>
      )}

      {/* Loading */}
      {isLoading && !data && (
        <div className="text-sm text-neutral-500 px-2">Loading metrics…</div>
      )}

      {/* Latest snapshot */}
      <div className="flex-1 min-h-0 overflow-auto space-y-4">
        {latest ? (
          <>
            <div className="flex items-baseline justify-between px-1">
              <h3 className="text-sm font-semibold text-neutral-200">Latest Snapshot</h3>
              <span className="text-[10px] text-neutral-500" title={latest.ts}>
                {timeAgo(latest.ts)}
              </span>
            </div>
            <SnapshotView s={latest} />
          </>
        ) : !isLoading ? (
          <div className="text-sm text-neutral-500 px-2">
            No metrics collected yet. Click <strong>Collect Now</strong> to request a snapshot.
          </div>
        ) : null}

        {/* History list */}
        {snapshots.length > 1 && (
          <details className="group">
            <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-300 px-1">
              {snapshots.length} historical snapshots
            </summary>
            <div className="mt-2 space-y-3 max-h-80 overflow-auto">
              {snapshots.slice(1).map((s, i) => (
                <div key={s.ts + i} className="pac-glass rounded-lg p-3 space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-mono text-neutral-400">{s.agentId}</span>
                    <span className="text-[10px] text-neutral-600" title={s.ts}>{timeAgo(s.ts)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs font-mono text-neutral-300">
                    <span>CPU {s.cpu != null ? `${s.cpu.toFixed(1)}%` : '—'}</span>
                    <span>Mem {s.memoryPercent != null ? `${s.memoryPercent.toFixed(1)}%` : '—'}</span>
                    <span>{s.disks.length} disk{s.disks.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Footer */}
      {data && (
        <div className="text-[10px] text-neutral-600 px-1 shrink-0">
          {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} — auto-refreshing every 10 s
        </div>
      )}
    </div>
  );
}
